import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldProse, NumberedSection, PanelCard, RegaraNoteList } from './DossierPrimitives';
import {
  fetchClinicalRequirements,
  type ClinicalRequirement,
  type ClinicalRequirementsResult,
} from '@/features/dossier/api';

/**
 * EU MDR Clinical Requirements panel.
 *
 * Split into:
 *   - `ClinicalRequirementsRenderer` — presentational, accepts a
 *     `ClinicalRequirementsResult` as a prop. Unit-testable without fetch.
 *   - `ClinicalRequirementsPanel` — container; fetches via
 *     `fetchClinicalRequirements` and delegates to the renderer.
 *
 * Renders:
 *   Cover block (device class)
 *   §1 Clinical Requirements (each requirement: name, applicability, basis, notes)
 *   Notes block (when present)
 *   Download PDF / DOCX links targeting
 *     GET …/clinical-requirements?device_class=…&format=pdf|docx
 *
 * Types are single-sourced in `@/features/dossier/api` — NOT re-declared here.
 */

// Re-export types so existing importers (e.g. tests) keep resolving.
export type { ClinicalRequirement, ClinicalRequirementsResult };

// ── Presentational renderer ───────────────────────────────────────────────────

export function ClinicalRequirementsRenderer({
  result,
  productId,
  deviceClass,
  hideDownload = false,
}: {
  result: ClinicalRequirementsResult;
  productId: number;
  deviceClass: string;
  hideDownload?: boolean;
}) {
  const baseUrl = `/api/products/${productId}/clinical-requirements?device_class=${encodeURIComponent(deviceClass)}`;


  return (
    <div className="space-y-4">
      <ClinicalCoverBlock deviceClass={result.device_class} />

      {result.requirements.length > 0 ? (
        <RequirementsSections requirements={result.requirements} />
      ) : (
        <NumberedSection prefix="Section" num={1} title="Clinical Requirements">
          <p className="text-[13.5px] text-muted italic m-0">No requirements specified.</p>
        </NumberedSection>
      )}

      <RegaraNoteList notes={result.notes} />

      {!hideDownload && <DownloadBar baseUrl={baseUrl} />}
    </div>
  );
}

// ── Cover block ───────────────────────────────────────────────────────────────

function ClinicalCoverBlock({ deviceClass }: { deviceClass: string }) {
  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU MDR Clinical Requirements
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/745 — Article 61 / Annexes XIV–XV
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
            Device class (EU MDR)
          </div>
          <div className="text-[13px] text-navy font-semibold">{deviceClass}</div>
        </div>
      </div>
    </PanelCard>
  );
}

// ── One numbered section per requirement (mirrors Python _render_requirements_sections) ──

function RequirementsSections({
  requirements,
}: {
  requirements: ClinicalRequirement[];
}) {
  return (
    <>
      {requirements.map((req, i) => (
        <NumberedSection
          key={i}
          prefix="Section"
          num={i + 1}
          title={req.name || `Requirement ${i + 1}`}
        >
          <div className="border-l-2 border-line pl-4 py-1">
            <div className="text-[12.5px] text-muted mb-1.5">
              <span className="font-semibold">Applicability:</span> {req.applicability}
            </div>
            <FieldProse label="Basis:" body={req.basis} fallbackHint="Clinical basis" />
            {req.notes && (
              <div className="text-[12.5px] text-muted mt-1">
                <span className="font-semibold">Notes:</span> {req.notes}
              </div>
            )}
          </div>
        </NumberedSection>
      ))}
    </>
  );
}

// ── Download bar ──────────────────────────────────────────────────────────────

function DownloadBar({ baseUrl }: { baseUrl: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="brand" size="sm" asChild>
        <a
          href={`${baseUrl}&format=pdf`}
          target="_blank"
          rel="noopener"
          title="Download Clinical Requirements as PDF"
        >
          <Download className="size-3.5" /> Download PDF
        </a>
      </Button>
    </div>
  );
}

// ── Container (fetches data) ──────────────────────────────────────────────────

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; result: ClinicalRequirementsResult };

export function ClinicalRequirementsPanel({
  productId,
  deviceClass,
}: {
  productId: number;
  deviceClass: string;
}) {
  const [state, setState] = useState<FetchState>({ status: 'idle' });

  const load = async () => {
    setState({ status: 'loading' });
    try {
      const result = await fetchClinicalRequirements(productId, deviceClass);
      setState({ status: 'ok', result });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to load clinical requirements.',
      });
    }
  };

  if (state.status === 'idle') {
    return (
      <PanelCard>
        <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-3">
          EU MDR Clinical Requirements
        </div>
        <p className="text-[13px] text-muted m-0 mb-4">
          Generate a clinical requirements summary for this device class.
        </p>
        <Button variant="brand" size="sm" onClick={() => void load()}>
          Load Clinical Requirements
        </Button>
      </PanelCard>
    );
  }

  if (state.status === 'loading') {
    return (
      <PanelCard>
        <p className="text-[13px] text-muted italic m-0">
          Loading clinical requirements…
        </p>
      </PanelCard>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
        <div className="font-semibold text-state-error-text text-[14px] mb-1">
          Could not load clinical requirements
        </div>
        <p className="text-[13px] text-state-error-text m-0 mb-3">{state.message}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ClinicalRequirementsRenderer
      result={state.result}
      productId={productId}
      deviceClass={deviceClass}
    />
  );
}
